import { useState } from 'react';
import { ChildPerson } from 'assets';
import { Child, ChildGuardian, GuardianFormFields, NonNullableObject } from 'types';
import { ParentAndGuardianFormItemContentContainer } from 'components/molecules';
import { GuardianModalAddEditForm, GuardianTable } from 'components/templates/guardian';
import { useGuardianOrParent } from 'hooks/useGuardianOrParent/useGuardianOrParent';
import { useGuardianMutate } from 'hooks/useGuardianMutate/useGuardianMutate';
import { ComponentBlockingAppLoader, DetailsFrameBase } from 'components/atoms';
import { parseChildGuardianIntoGuardianFormFieldsWithFormattedPhoneNumber } from 'utils/parsers/guardian/parseChildGuardianIntoGuardianFormFieldsWithFormattedPhoneNumber';
import { parseGuardianFormFieldsIntoChildGuardian } from 'utils/parsers/guardian/parseGuardianFormFieldsIntoChildGuardian';
import { childrenTexts } from 'consts/text';
import { useChildAbilities } from 'abilities';

export type GuardiansProps = NonNullableObject<Pick<Child, 'guardians' | 'id' | 'status'>>;

export const Guardians = ({ guardians, id, status: childStatus }: GuardiansProps) => {
    const [editingGuardian, setEditingGuardian] = useState<ChildGuardian | null>(null);
    const { addGuardian, deleteGuardian, updateGuardian, isGuardianMutating } = useGuardianMutate({
        childId: id,
    });
    const {
        isModalFormOpen,
        handleModalFormOpen,
        handleModalFormClose,
        handleDataItemAdd: handleGuardianAdd,
        handleDataItemEdit: handleGuardianEdit,
        handleDataItemRemove: handleGuardianRemove,
        handleTableDataItemEditClick: handleGuardianTableItemEditClick,
    } = useGuardianOrParent<ChildGuardian>({
        onItemAdd: (childGuardian) => {
            addGuardian({
                childId: id,
                childGuardian,
            });
        },
        onItemEditFinish: (childGuardian) => {
            updateGuardian({
                childId: id,
                guardianId: childGuardian.id,
                childGuardian,
            });
        },
        onItemRemove: (childGuardian) => {
            deleteGuardian({ childId: id, guardianId: childGuardian.id });
        },
        onItemToEditPick: (childGuardian) => {
            setEditingGuardian(childGuardian);
        },
        clearSelectedItem: () => setEditingGuardian(null),
    });
    const { childAbilities } = useChildAbilities();

    const canManageChildDetails =
        childStatus === 'ACTIVE' && childAbilities.checkIfCanManageChildrenList();
    const isSelectedGuardianTableItemToEdit = !!editingGuardian;
    const modalFormMode = isSelectedGuardianTableItemToEdit ? 'edit' : 'add';
    const formName = isSelectedGuardianTableItemToEdit ? 'guardianEdit' : 'guardianAdd';

    const modalFormSubmitFormHandler = (data: GuardianFormFields) => {
        const formData = parseGuardianFormFieldsIntoChildGuardian(data, editingGuardian);
        return isSelectedGuardianTableItemToEdit
            ? handleGuardianEdit(formData)
            : handleGuardianAdd(formData);
    };

    return (
        <DetailsFrameBase
            icon={<ChildPerson />}
            title={childrenTexts.childDetails.guardians.guardianData}
            noFrame
            smallHeaderMargin
        >
            <GuardianModalAddEditForm
                open={isModalFormOpen}
                mode={modalFormMode}
                onSubmitForm={modalFormSubmitFormHandler}
                onCancel={handleModalFormClose}
                formName={formName}
                initialValues={
                    isSelectedGuardianTableItemToEdit
                        ? parseChildGuardianIntoGuardianFormFieldsWithFormattedPhoneNumber(
                              editingGuardian,
                          )
                        : undefined
                }
            />
            <ParentAndGuardianFormItemContentContainer
                addButtonText={childrenTexts.addGuardianButtonLabel}
                onAddButtonClick={handleModalFormOpen}
                isInvisibleAddButton={!canManageChildDetails}
                tableElement={
                    <GuardianTable
                        guardians={guardians}
                        onEditGuardianClick={handleGuardianTableItemEditClick}
                        onRemoveGuardianClick={handleGuardianRemove}
                        testId="Guardians_table"
                        canManageTable={canManageChildDetails}
                    />
                }
            />
            {isGuardianMutating && <ComponentBlockingAppLoader />}
        </DetailsFrameBase>
    );
};
